/**
 * Single source of truth for the client-matching request vocabulary and the
 * response-time promise. Mirrors `categories.ts` for the directory taxonomy.
 *
 * The DB CHECK constraints on `public.requests` (status / timeline /
 * category) mirror these values EXACTLY — changing a value here requires a
 * Supabase migration updating the matching constraint, and vice versa.
 */

import { byLocale } from "@/lib/locale";
import { DIRECTORY_CATEGORIES, getCategoryLabel, type DirectoryCategory } from "./categories";

// ── Status ───────────────────────────────────────────────────────────────────

export const REQUEST_STATUSES = ["new", "in_progress", "closed"] as const;

export type RequestStatus = (typeof REQUEST_STATUSES)[number];

export const REQUEST_STATUS_LABELS: Record<RequestStatus, string> = {
  new:         "New",
  in_progress: "In progress",
  closed:      "Closed",
};

// ── Form vocabularies ────────────────────────────────────────────────────────

/** "Other" is a requests-only category — deliberately NOT part of
 *  DIRECTORY_CATEGORIES, which drives the directory taxonomy. */
export const REQUEST_CATEGORY_OPTIONS = [...DIRECTORY_CATEGORIES, "Other"] as const;

/** The timing a reader may VOLUNTEER in the chat (its `timeline` slot), and
 *  the values `requests.timeline` accepts. Neither the chat nor the /request
 *  form asks for it since 2026-10-01 (owner): it filtered nothing. */
export const REQUEST_TIMELINE_OPTIONS = [
  "ASAP",
  "Within a month",
  "1-3 months",
  "Just exploring",
] as const;

// "Other" is the requests-only category with no directory display label.
const REQUEST_OTHER_LABEL_VI = "Khác";

/** Category options as `{ value, label }`. Directory categories localize via
 *  getCategoryLabel; the requests-only "Other" gets its own localized label. */
export function getRequestCategoryOptions(locale: string): { value: string; label: string }[] {
  return REQUEST_CATEGORY_OPTIONS.map((value) => ({
    value,
    label: value === "Other"
      ? byLocale(locale, { en: "Other", vi: REQUEST_OTHER_LABEL_VI, fr: "Autre", "zh-Hans": "其他", "zh-Hant": "其他" })
      : getCategoryLabel(value as DirectoryCategory, locale),
  }));
}

// ── Response-time promise ────────────────────────────────────────────────────
//
// The public promise ("introductions within 48 hours") and the admin
// countdown both derive from this — change it here and every prose mention,
// the deadline math, and the urgency tiers move together.

export const RESPONSE_SLA_HOURS = 48;

export const RESPONSE_SLA_MS = RESPONSE_SLA_HOURS * 60 * 60 * 1000;

/** Remaining time at which the admin card flips to the gold "near deadline"
 *  tier. */
export const RESPONSE_SLA_NEAR_MS = 12 * 60 * 60 * 1000;
